import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDateTime } from '../utils/dateFormat';
import './TransactionOverview.css';

function TransactionOverview() {
  const { apiFetch } = useAuth();
  const { language } = useI18n();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 100, offset: 0, pages: 1 });
  const [filters, setFilters] = useState({ categories: [], types: [], moneyTypes: [] });
  
  // Filter state
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMoneyType, setSelectedMoneyType] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const t = language === 'da'
    ? {
        transactionOverview: 'Transaktionsoversigt',
        filters: 'Filtre',
        transactionType: 'Transaktionstype',
        category: 'Kategori',
        moneyType: 'Pengetype',
        loadingTransactions: 'Indlæser transaktioner...',
        allTypes: 'Alle typer',
        allCategories: 'Alle kategorier',
        allMoneyTypes: 'Alle pengetyper',
        id: 'ID',
        type: 'Type',
        player: 'Spiller',
        amount: 'Beløb',
        reason: 'Årsag',
        date: 'Dato',
        balance: 'Saldo efter',
        from: 'Fra',
        to: 'Til',
        empty: 'Ingen transaktioner fundet',
        page: 'Side',
        of: 'af',
        faucet: 'Tilførsel',
        sink: 'Afledning',
        transfer: 'Overførsel',
        cash: 'Kontanter',
        bank: 'Bank',
        dirty: 'Sort penge',
        crypto: 'Krypto',
        seeMore: 'Se mere',
        seeLess: 'Se mindre'
      }
    : {
        transactionOverview: 'Transaction Overview',
        filters: 'Filters',
        transactionType: 'Transaction Type',
        category: 'Category',
        moneyType: 'Money Type',
        loadingTransactions: 'Loading transactions...',
        allTypes: 'All Types',
        allCategories: 'All Categories',
        allMoneyTypes: 'All Money Types',
        id: 'ID',
        type: 'Type',
        player: 'Player',
        amount: 'Amount',
        reason: 'Reason',
        date: 'Date',
        balance: 'Balance After',
        from: 'From',
        to: 'To',
        empty: 'No transactions found',
        page: 'Page',
        of: 'of',
        faucet: 'Faucet',
        sink: 'Sink',
        transfer: 'Transfer',
        cash: 'Cash',
        bank: 'Bank',
        dirty: 'Black Money',
        crypto: 'Crypto',
        seeMore: 'See more',
        seeLess: 'See less'
      };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'da' ? 'da-DK' : 'en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return formatEuDateTime(dateString);
  };

  const getTypeDisplay = (typeKey) => {
    const typeMap = {
      'faucet': t.faucet,
      'sink': t.sink,
      'transfer': t.transfer
    };
    return typeMap[typeKey] || typeKey;
  };

  const getMoneyTypeDisplay = (moneyTypeKey) => {
    const moneyMap = {
      'cash': t.cash,
      'bank': t.bank,
      'dirty': t.dirty,
      'crypto': t.crypto
    };
    return moneyMap[moneyTypeKey] || moneyTypeKey;
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 100,
        offset: currentPage * 100
      });

      if (selectedType) params.append('type', selectedType);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedMoneyType) params.append('moneyType', selectedMoneyType);

      const response = await apiFetch(`/api/economy/transactions?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch transactions');
      
      const data = await response.json();
      setTransactions(data.transactions || []);
      setPagination(data.pagination || {});
      setFilters(data.filters || {});
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, currentPage, selectedType, selectedCategory, selectedMoneyType]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleFilterChange = (type, value) => {
    if (type === 'type') setSelectedType(value);
    else if (type === 'category') setSelectedCategory(value);
    else if (type === 'moneyType') setSelectedMoneyType(value);
    setCurrentPage(0);
  };

  const getPlayerDisplayName = (identifier) => {
    if (!identifier) return '—';
    // If it contains a colon, show just the part after (usually the player name)
    const parts = identifier.split(':');
    return parts[parts.length - 1].substring(0, 8) + (parts[parts.length - 1].length > 8 ? '...' : '');
  };

  const toggleRowExpansion = (transactionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(transactionId)) {
      newExpanded.delete(transactionId);
    } else {
      newExpanded.add(transactionId);
    }
    setExpandedRows(newExpanded);
  };

  const renderPlayerCell = (identifier, transactionId, isFromColumn) => {
    const isExpanded = expandedRows.has(`${transactionId}-${isFromColumn ? 'from' : 'to'}`);
    const cellKey = `${transactionId}-${isFromColumn ? 'from' : 'to'}`;
    
    if (!identifier) return '—';
    
    return (
      <div className="player-cell-wrapper">
        <div className={`player-cell ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? identifier : getPlayerDisplayName(identifier)}
        </div>
        {identifier && identifier.length > 8 && (
          <button
            className="expand-button"
            onClick={() => toggleRowExpansion(cellKey)}
            title={isExpanded ? t.seeLess : t.seeMore}
          >
            {isExpanded ? '−' : '+'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="transaction-overview">
      <div className="transaction-header">
        <h2>{t.transactionOverview}</h2>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>{t.transactionType}</label>
          <select value={selectedType} onChange={(e) => handleFilterChange('type', e.target.value)}>
            <option value="">{t.allTypes}</option>
            {filters.types?.map(type => (
              <option key={type} value={type}>{getTypeDisplay(type)}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t.category}</label>
          <select value={selectedCategory} onChange={(e) => handleFilterChange('category', e.target.value)}>
            <option value="">{t.allCategories}</option>
            {filters.categories?.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t.moneyType}</label>
          <select value={selectedMoneyType} onChange={(e) => handleFilterChange('moneyType', e.target.value)}>
            <option value="">{t.allMoneyTypes}</option>
            {filters.moneyTypes?.map(mt => (
              <option key={mt} value={mt}>{getMoneyTypeDisplay(mt)}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">{t.loadingTransactions}</div>
      ) : transactions.length === 0 ? (
        <div className="empty-state">{t.empty}</div>
      ) : (
        <>
          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>{t.id}</th>
                  <th>{t.type}</th>
                  <th>{t.category}</th>
                  <th>{t.moneyType}</th>
                  <th>{t.amount}</th>
                  <th>{t.from}</th>
                  <th>{t.to}</th>
                  <th>{t.reason}</th>
                  <th>{t.balance}</th>
                  <th>{t.date}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((trans) => (
                  <tr key={trans.id} className={`type-${trans.transaction_type}`}>
                    <td className="id">{trans.id}</td>
                    <td className="type"><span className={`type-badge ${trans.transaction_type}`}>{getTypeDisplay(trans.transaction_type)}</span></td>
                    <td className="category">{trans.category}</td>
                    <td className="money-type"><span className={`money-badge ${trans.money_type}`}>{getMoneyTypeDisplay(trans.money_type)}</span></td>
                    <td className={`amount ${trans.transaction_type === 'faucet' ? 'positive' : 'negative'}`}>{formatCurrency(trans.amount)}</td>
                    <td>{renderPlayerCell(trans.player_from, trans.id, true)}</td>
                    <td>{renderPlayerCell(trans.player_to, trans.id, false)}</td>
                    <td className="reason" title={trans.reason}>{trans.reason || '—'}</td>
                    <td className="balance">{formatCurrency(trans.balance_after)}</td>
                    <td className="date">{formatDate(trans.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                ← {language === 'da' ? 'Tilbage' : 'Previous'}
              </button>
              <span className="page-info">
                {t.page} {currentPage + 1} {t.of} {pagination.pages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages - 1, currentPage + 1))}
                disabled={currentPage >= pagination.pages - 1}
              >
                {language === 'da' ? 'Næste' : 'Next'} →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TransactionOverview;
